import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Logo from "./Logo";
import { LOGO_UPGRADE_MODAL } from "../config/logoSizes";
import { LegalModal, TERMS_AR, TERMS_EN, PRIVACY_AR, PRIVACY_EN } from "../pages/WelcomeScreen";
import { useApp } from "../context/AppContext";
import { isIOSApp, IOS_MAX_HOURS, getAppleProducts, purchaseApplePlan, restoreApplePurchases } from "../utils/appleIAP";
const Feature = ({ icon, bold, label }) => <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
    <div style={{
  width: 32,
  height: 32,
  borderRadius: 9,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 16,
  background: "var(--bg-hover)"
}}>{icon}</div>
    <span style={{ fontSize: 12.5, color: "var(--text-1)", lineHeight: 1.35 }}>
      <strong style={{ fontWeight: 700 }}>{bold} </strong>{label}
    </span>
  </div>;
const UpgradeModal = ({ onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const { checkAuth } = useApp();
  // iOS app: purchases must go through Apple IAP (App Review 3.1.1) — EdfaPay serves web/Android
  const iosApp = isIOSApp();
  const maxHours = iosApp ? IOS_MAX_HOURS : 48;
  const [appleProducts, setAppleProducts] = useState(null);
  const [legalModal, setLegalModal] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [alert, setAlert] = useState(null);
  const [hours, setHours] = useState(1);
  const [hoursInput, setHoursInput] = useState("1");
  const [planInfo, setPlanInfo] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  useEffect(() => {
    if (!iosApp) return;
    getAppleProducts()
      .then((p) => {
        setAppleProducts(p);
        // Prices render as "…" whenever StoreKit gives us nothing. Say so out
        // loud instead of leaving a silent placeholder the user can't act on.
        if (p?.error) {
          setAlert({
            type: "error",
            msg: isAr
              ? "تعذّر تحميل الأسعار من App Store. تأكد من إعداد المنتجات في App Store Connect."
              : "Could not load App Store prices. Check the products are configured in App Store Connect."
          });
        }
      })
      .catch((e) => {
        console.error("[AppleIAP] product load rejected:", e);
        setAppleProducts(null);
      });
  }, [iosApp, isAr]);
  const hourlyPlan = planInfo?.plans?.find((p) => p.id === "hourly");
  const monthlyPlan = planInfo?.plans?.find((p) => p.id === "monthly");
  const hourlyPrice = hourlyPlan?.amount || 1;
  const monthlyPrice = monthlyPlan?.amount || 25;
  const currencyCode = planInfo?.locale?.currency || "USD";
  const dailyIfHourly = hourlyPrice * 24;
  const dailyIfMonthly = monthlyPrice / 30;
  const savingsPercent = Math.round((dailyIfHourly - dailyIfMonthly) / dailyIfHourly * 100);
  const formatPrice = (value, currency = "USD") => {
    try {
      return new Intl.NumberFormat(i18n.language || "en-US", {
        style: "currency",
        currency,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    } catch (err) {
      return `${Number(value || 0).toFixed(2)} ${currency}`;
    }
  };
  // On iOS the displayed price must be StoreKit's priceString (App Store review requirement)
  const hourPriceLabel = iosApp ? appleProducts?.hour ? hours > 1 ? `${hours} × ${appleProducts.hour.priceString}` : appleProducts.hour.priceString : "..." : formatPrice(hours * hourlyPrice, currencyCode);
  const monthlyPriceLabel = iosApp ? appleProducts?.monthly?.priceString || "..." : planLoading ? "..." : formatPrice(monthlyPrice, currencyCode);
  const lang = i18n.language;
  const termsContent = lang === "ar" ? TERMS_AR : TERMS_EN;
  const privacyContent = lang === "ar" ? PRIVACY_AR : PRIVACY_EN;
  useEffect(() => {
    let active = true;
    const loadPlans = async () => {
      try {
        const res = await axios.get("/api/payment/plans");
        if (!active) return;
        setPlanInfo(res.data);
      } catch (err) {
        console.error("Failed to load plans:", err);
        setAlert({ type: "error", msg: t("paymentFailed") });
      } finally {
        setPlanLoading(false);
      }
    };
    loadPlans();
    return () => {
      active = false;
    };
  }, [t]);
  const handleHoursInput = (val) => {
    setHoursInput(val);
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 1 && n <= maxHours) setHours(n);
  };
  const handleHoursBlur = () => {
    const n = parseInt(hoursInput, 10);
    if (isNaN(n) || n < 1) {
      setHours(1);
      setHoursInput("1");
    } else if (n > maxHours) {
      setHours(maxHours);
      setHoursInput(String(maxHours));
    } else {
      setHours(n);
      setHoursInput(String(n));
    }
  };
  // The user can dismiss Apple's payment sheet themselves — not an error worth a red alert
  const isUserCancelled = (err) => /cancel|cancelled|canceled/i.test(String(err?.message || err?.code || ""));
  const handleBuyHourly = async () => {
    setLoadingPlan("hourly");
    setAlert(null);
    try {
      if (iosApp) {
        await purchaseApplePlan("hourly", hours);
        await checkAuth();
        onSuccess?.();
        return;
      }
      const sessionRes = await axios.post("/api/payment/create-session", { hours });
      const paymentUrl = sessionRes?.data?.paymentUrl;
      if (!paymentUrl) throw new Error("Missing payment URL");
      window.location.assign(paymentUrl);
    } catch (err) {
      if (!isUserCancelled(err)) setAlert({ type: "error", msg: err.response?.data?.message || t("paymentFailed") });
    } finally {
      setLoadingPlan(null);
    }
  };
  const handleBuyMonthly = async () => {
    setLoadingPlan("monthly");
    setAlert(null);
    try {
      if (iosApp) {
        await purchaseApplePlan("monthly");
        await checkAuth();
        onSuccess?.();
        return;
      }
      const sessionRes = await axios.post("/api/payment/buy-monthly");
      const paymentUrl = sessionRes?.data?.paymentUrl;
      if (!paymentUrl) throw new Error("Missing payment URL");
      window.location.assign(paymentUrl);
    } catch (err) {
      if (!isUserCancelled(err)) setAlert({ type: "error", msg: err.response?.data?.message || t("paymentFailed") });
    } finally {
      setLoadingPlan(null);
    }
  };
  const handleRestore = async () => {
    setLoadingPlan("restore");
    setAlert(null);
    try {
      const restored = await restoreApplePurchases();
      await checkAuth();
      if (restored) {
        setAlert({ type: "success", msg: isAr ? "تمت استعادة مشترياتك ✓" : "Purchases restored ✓" });
        onSuccess?.();
      } else {
        setAlert({ type: "error", msg: isAr ? "لم نجد مشتريات سابقة لاستعادتها" : "No previous purchases found to restore" });
      }
    } catch (err) {
      setAlert({ type: "error", msg: err.response?.data?.message || t("paymentFailed") });
    } finally {
      setLoadingPlan(null);
    }
  };
  return <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box upgrade-modal-box" style={{
    maxWidth: 860,
    width: "95vw",
    maxHeight: "92dvh"
  }}>
        <div className="upgrade-modal-scroll" style={{ overflow: legalModal ? "hidden" : "auto" }}>
          {
    
  }
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, marginBottom: 14 }}>
            <Logo variant="full" size={LOGO_UPGRADE_MODAL} className="upgrade-modal-brand-logo auth-logo-img" />
            <p className="modal-sub" style={{ margin: "2px 0 0", fontSize: 13 }}>{t(".")}</p>
            <h2 className="modal-title" style={{ margin: "2px 0 0", fontSize: 18 }}>{t("upgradePlan")}</h2>
            <p className="modal-sub" style={{ margin: "2px 0 0", fontSize: 13 }}>{t(" ")}</p>
          </div>
          {!planLoading && planInfo && <p style={{ textAlign: "center", fontSize: 11.5, color: "var(--text-3)", margin: "0 0 8px" }}>
              {iosApp ? isAr ? "\u0627\u0644\u0623\u0633\u0639\u0627\u0631 \u0645\u0646 App Store \u0628\u0639\u0645\u0644\u0629 \u062D\u0633\u0627\u0628\u0643" : "Prices from the App Store in your account currency" : isAr ? `\u0627\u0644\u0623\u0633\u0639\u0627\u0631 \u0628\u0639\u0645\u0644\u0629 ${planInfo?.locale?.countryName || "\u062F\u0648\u0644\u062A\u0643"} (${currencyCode})` : `Prices shown in ${planInfo?.locale?.countryName || "your country"} currency (${currencyCode})`}
            </p>}
          {alert && <div className={`form-alert ${alert.type}`} style={{ marginBottom: 10 }}>{alert.msg}</div>}
          <div className="upgrade-modal-inner-row" style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 190px", minWidth: 170 }}>
              <Feature icon="💬" bold={t("welcome10bold")} label={t("welcome10label")} />
              <Feature icon="🌙" bold={t("welcomeDreamBold")} label={t("welcomeDreamLabel")} />
              <Feature icon="🔒" bold={t("welcomePrivacyBold")} label={t("welcomePrivacyLabel")} />
              <Feature icon="⚡" bold={t("welcomeUpgradeBold")} label={t("welcomeUpgradeLabel")} />
            </div>
            <div className="upgrade-plans-row" style={{ flex: "2 1 400px", display: "flex", gap: 10, alignItems: "stretch", flexWrap: "nowrap" }}>
              <div className="upgrade-plan-card" style={{ flex: 1, minWidth: 110 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: 1 }}>🆓 {t("freePlan")}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text-2)", fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>{formatPrice(0, currencyCode)}</div>
                <div style={{ fontSize: 11, color: "var(--text-3)", flex: 1 }}>
                  <div>✓ {t("freePlanNote")}</div>
                  <div>✓ {t("welcomeFreeF2")}</div>
                </div>
                <button
    onClick={onClose}
    style={{
      width: "100%",
      padding: "8px",
      marginTop: 6,
      background: "transparent",
      color: "var(--text-2)",
      border: "1.5px solid var(--border3)",
      borderRadius: 9,
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      transition: "all .15s"
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = "var(--accent)";
      e.currentTarget.style.color = "var(--text-1)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = "var(--border3)";
      e.currentTarget.style.color = "var(--text-2)";
    }}
  >{isAr ? "\u0627\u0633\u062A\u0645\u0631 \u0628\u0627\u0644\u0645\u062C\u0627\u0646\u064A" : "Continue with Free Plan"}</button>
              </div>
              <div className="upgrade-plan-card" style={{ flex: 1.1, minWidth: 120 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: 1 }}>⏱ {t("hourlyPlan")}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "var(--accent)", fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>
                  {hourPriceLabel}
                  <span style={{ fontSize: 11, fontWeight: 400, color: "var(--text-3)", marginLeft: 3 }}>/ {hours === 1 ? isAr ? "\u0633\u0627\u0639\u0629" : "hr" : isAr ? "\u0633\u0627\u0639\u0627\u062A" : "hrs"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "4px 0" }}>
                  <button className="hours-btn" onClick={() => {
    const n = Math.max(1, hours - 1);
    setHours(n);
    setHoursInput(String(n));
  }} style={{ width: 24, height: 24, fontSize: 15 }}>−</button>
                  <input
    type="number"
    value={hoursInput}
    min={1}
    max={maxHours}
    onChange={(e) => handleHoursInput(e.target.value)}
    onBlur={handleHoursBlur}
    style={{
      flex: 1,
      textAlign: "center",
      fontSize: 13,
      fontWeight: 700,
      color: "var(--text-1)",
      background: "var(--bg-base)",
      border: "1px solid var(--border2)",
      borderRadius: 7,
      padding: "3px 4px",
      outline: "none",
      width: "100%",
      fontFamily: "inherit"
    }}
  />
                  <span style={{ fontSize: 10, color: "var(--text-3)" }}>{isAr ? "\u0633\u0627\u0639\u0629" : "hr"}</span>
                  <button className="hours-btn" onClick={() => {
    const n = Math.min(maxHours, hours + 1);
    setHours(n);
    setHoursInput(String(n));
  }} style={{ width: 24, height: 24, fontSize: 15 }}>+</button>
                </div>
                <div style={{ fontSize: 11.5, color: "var(--text-2)", flex: 1 }}>✨ {t("unlimitedMessages")}</div>
                <button onClick={handleBuyHourly} disabled={loadingPlan === "hourly" || (iosApp && !appleProducts?.hour)} className="plan-btn" style={{ marginTop: 6, padding: "8px", fontSize: 12 }}>
                  {loadingPlan === "hourly" ? <><div className="spinner" />{isAr ? "\u062C\u0627\u0631\u064A..." : iosApp ? "Processing..." : "Redirecting..."}</> : `${isAr ? "\u0627\u062F\u0641\u0639 \u0627\u0644\u0622\u0646" : "Pay Now"} \u2014 ${hourPriceLabel}`}
                </button>
              </div>
              <div className="upgrade-plan-card upgrade-plan-card--pro" style={{ flex: 1.2, minWidth: 130 }}>
                <div style={{
    position: "absolute",
    top: -10,
    left: "50%",
    transform: "translateX(-50%)",
    background: "var(--accent)",
    color: "#fff",
    fontSize: 9.5,
    fontWeight: 700,
    padding: "2px 10px",
    borderRadius: 9,
    whiteSpace: "nowrap"
  }}>⚡ {isAr ? "\u0627\u0644\u0623\u0641\u0636\u0644 \u0642\u064A\u0645\u0629" : "Best Value"}</div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: 1 }}>👑 {t("proPlan")}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "var(--accent)", fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>
                  {monthlyPriceLabel}
                  <span style={{ fontSize: 11, fontWeight: 400, color: "var(--text-3)", marginLeft: 3 }}>/ {isAr ? "\u0634\u0647\u0631" : "month"}</span>
                </div>
                <div style={{ fontSize: 11.5, color: "var(--text-2)", flex: 1 }}>
                  <div>✨ {t("proUnlimited")}</div>
                  {!iosApp && !planLoading && savingsPercent > 0 && <div style={{
    marginTop: 5,
    padding: "4px 7px",
    background: "rgba(61,186,110,.1)",
    border: "1px solid rgba(61,186,110,.2)",
    borderRadius: 7,
    fontSize: 10.5
  }}>
                      <span style={{ color: "var(--accent)", fontWeight: 800 }}>🔥 {isAr ? `\u0648\u0641\u0631 ${savingsPercent}%` : `Save ${savingsPercent}%`}</span>
                      <br />
                      <span style={{ color: "var(--text-3)", fontSize: 10 }}>
                        {isAr ? `\u0628\u062F\u0644 ${formatPrice(dailyIfHourly, currencyCode)}/\u064A\u0648\u0645 \u2192 ${formatPrice(dailyIfMonthly, currencyCode)}/\u064A\u0648\u0645` : `Instead of ${formatPrice(dailyIfHourly, currencyCode)}/day \u2192 Only ${formatPrice(dailyIfMonthly, currencyCode)}/day`}
                      </span>
                    </div>}
                </div>
                <button onClick={handleBuyMonthly} disabled={loadingPlan === "monthly" || (iosApp && !appleProducts?.monthly)} className="plan-btn pro-btn" style={{ marginTop: 6, padding: "8px", fontSize: 12 }}>
                  {loadingPlan === "monthly" ? <><div className="spinner" />{isAr ? "\u062C\u0627\u0631\u064A..." : iosApp ? "Processing..." : "Redirecting..."}</> : `${isAr ? "\u0627\u0634\u062A\u0631\u0643 \u0627\u0644\u0622\u0646" : "Subscribe Now"} \u2014 ${monthlyPriceLabel}`}
                </button>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 10, fontSize: 12, color: "var(--text-3)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <button className="legal-link-btn" onClick={() => setLegalModal("terms")}>{t("termsOfUse")}</button>
              <span style={{ opacity: 0.4 }}>·</span>
              <button className="legal-link-btn" onClick={() => setLegalModal("privacy")}>{t("privacyPolicy")}</button>
              {iosApp && <>
                <span style={{ opacity: 0.4 }}>·</span>
                <button className="legal-link-btn" onClick={handleRestore} disabled={loadingPlan === "restore"}>
                  {loadingPlan === "restore" ? isAr ? "جاري الاستعادة..." : "Restoring..." : isAr ? "استعادة المشتريات" : "Restore Purchases"}
                </button>
              </>}
            </span>
          </div>
        </div>
      </div>
      {legalModal === "terms" && <LegalModal title={t("termsOfUse")} content={termsContent} isAr={lang === "ar"} onClose={() => setLegalModal(null)} />}
      {legalModal === "privacy" && <LegalModal title={t("privacyPolicy")} content={privacyContent} isAr={lang === "ar"} onClose={() => setLegalModal(null)} />}
    </div>;
};
var stdin_default = UpgradeModal;
export {
  stdin_default as default
};
